import dayjs from "dayjs";

export type DashboardScope = "mine" | "all";
export type DashboardPeriod = "day" | "week" | "prev_week" | "month" | "prev_month" | "year" | "historic";

export const getPeriodDateFilter = (period: DashboardPeriod): string | null => {
  if (period === "historic") return null;
  const map: Record<string, [dayjs.Dayjs, dayjs.Dayjs]> = {
    day:        [dayjs().startOf("day"),            dayjs().endOf("day")],
    week:       [dayjs().startOf("week"),           dayjs().endOf("week")],
    prev_week:  [dayjs().subtract(1, "week").startOf("week"), dayjs().subtract(1, "week").endOf("week")],
    month:      [dayjs().startOf("month"),          dayjs().endOf("month")],
    prev_month: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")],
    year:       [dayjs().startOf("year"),           dayjs().endOf("year")],
  };
  const [start, end] = map[period];
  return `$btw:${start.toISOString()},${end.toISOString()}`;
};

export const formatAmount = (value: number): string =>
  `$${new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}`;

export const getPreviousPeriodDateFilter = (period: DashboardPeriod): string | null => {
  if (period === "historic") return null;
  const map: Record<string, [dayjs.Dayjs, dayjs.Dayjs]> = {
    day:        [dayjs().subtract(1, "day").startOf("day"),   dayjs().subtract(1, "day").endOf("day")],
    week:       [dayjs().subtract(1, "week").startOf("week"), dayjs().subtract(1, "week").endOf("week")],
    prev_week:  [dayjs().subtract(2, "week").startOf("week"), dayjs().subtract(2, "week").endOf("week")],
    month:      [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")],
    prev_month: [dayjs().subtract(2, "month").startOf("month"), dayjs().subtract(2, "month").endOf("month")],
    year:       [dayjs().subtract(1, "year").startOf("year"),  dayjs().subtract(1, "year").endOf("year")],
  };
  const [start, end] = map[period];
  return `$btw:${start.toISOString()},${end.toISOString()}`;
};

/**
 * El nombre del período, con el mes real en vez de un genérico (QA #273.2).
 *
 * "Mes en curso" y "Mes anterior" no le dicen nada a quien mira un tablero: hay que saber en qué
 * mes estamos y restar. `Septiembre` y `Agosto` se leen de un vistazo. Lo mismo la comparativa,
 * que decía "Mes anterior vs anteanterior".
 *
 * Vive acá y no en cada widget porque son cuatro los que lo muestran, y ya se habían separado.
 */
const capitalizar = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * "del 8 al 14 de septiembre". "Esta semana" y "Semana anterior" no dicen qué días son —a
 * diferencia de "Mes en curso", donde el mes ya lo aclara— así que estos dos tabs muestran el
 * rango concreto en vez del nombre genérico (xcons-docs#280).
 */
const rangoDeFechas = (desde: dayjs.Dayjs, hasta: dayjs.Dayjs): string => {
  const mismoMes = desde.month() === hasta.month();
  const inicio = desde.format(mismoMes ? "D" : "D [de] MMMM");
  return `del ${inicio} al ${hasta.format("D [de] MMMM")}`;
};

export const nombreDePeriodo = (period: DashboardPeriod, hoy = dayjs()): string => {
  switch (period) {
    case "day":
      return "Hoy";
    case "week":
      return rangoDeFechas(hoy.startOf("week"), hoy.endOf("week"));
    case "prev_week":
      return rangoDeFechas(
        hoy.subtract(1, "week").startOf("week"),
        hoy.subtract(1, "week").endOf("week"),
      );
    case "month":
      return capitalizar(hoy.format("MMMM"));
    case "prev_month":
      return capitalizar(hoy.subtract(1, "month").format("MMMM"));
    case "year":
      return hoy.format("YYYY");
    case "historic":
      return "Histórico";
  }
};

/**
 * El mismo nombre, para el período anterior. `null` en `historic`, que no tiene anterior.
 *
 * **En los períodos en curso nombra el tramo, no el período** (D20, `xcons-docs#321`): la API
 * recorta el anterior a los mismos días transcurridos que lleva el actual, así que el 18 de
 * septiembre se compara contra el 1-18 de agosto y no contra agosto entero. Decir "Agosto" ahí
 * sería mentir sobre qué se está comparando.
 *
 * Los períodos **cerrados** —"Ayer", "Semana anterior", "Mes anterior"— se siguen nombrando: ahí la
 * comparación es completa contra completa y el rango no agrega nada.
 */
export const nombreDePeriodoAnterior = (
  period: DashboardPeriod,
  hoy = dayjs(),
): string | null => {
  switch (period) {
    case "day":
      return "Ayer";
    case "week":
      // Hasta el mismo día de semana que hoy: un viernes, lunes a viernes de la semana pasada.
      return rangoDeFechas(hoy.subtract(1, "week").startOf("week"), hoy.subtract(1, "week"));
    case "prev_week":
      return rangoDeFechas(
        hoy.subtract(2, "week").startOf("week"),
        hoy.subtract(2, "week").endOf("week"),
      );
    case "month": {
      // El mismo día del mes, recortado al último cuando ese mes es más corto: el 31/03 compara
      // contra febrero entero. `dayjs` ya hace el clamp al restar un mes.
      const anterior = hoy.subtract(1, "month");
      return rangoDeFechas(anterior.startOf("month"), anterior);
    }
    case "prev_month":
      return capitalizar(hoy.subtract(2, "month").format("MMMM"));
    case "year":
      return hoy.subtract(1, "year").format("YYYY");
    case "historic":
      return null;
  }
};

/** "Septiembre vs Agosto". Para el widget de comparativa. */
export const etiquetaComparativa = (period: DashboardPeriod, hoy = dayjs()): string => {
  const anterior = nombreDePeriodoAnterior(period, hoy);
  return anterior ? `${nombreDePeriodo(period, hoy)} vs ${anterior}` : nombreDePeriodo(period, hoy);
};

/**
 * Agrupa una serie diaria en las barras que corresponden a cada tab (QA #273.4).
 *
 * El agregado siempre viene por día. Dibujarlo crudo daba ~30 barras en "Mes en curso", y con
 * tantas G2 esconde las etiquetas del eje — de ahí el reporte de que la referencia sólo se veía al
 * pasar el mouse. Con 1, 7 o 4 barras entran todas.
 *
 * | Tab | Barras |
 * |---|---|
 * | Hoy | 1 |
 * | Esta semana | 7, una por día |
 * | Mes en curso / anterior | 4, una por semana (1-7, 8-14, 15-21, 22-fin) |
 * | Año | 12, una por mes |
 *
 * **Los huecos se rellenan con cero**: la serie sólo trae los días con datos, y una semana con tres
 * barras se lee como si la semana tuviera tres días.
 */
export function agruparSerie(
  serie: { date: string; count: number }[],
  period: DashboardPeriod,
  hoy = dayjs(),
): { d: string; sub?: string; n: number }[] {
  const porFecha = new Map(serie.map((p) => [p.date.slice(0, 10), p.count]));
  const sumar = (desde: dayjs.Dayjs, hasta: dayjs.Dayjs) => {
    let n = 0;
    for (let d = desde; d.isBefore(hasta) || d.isSame(hasta, "day"); d = d.add(1, "day")) {
      n += porFecha.get(d.format("YYYY-MM-DD")) ?? 0;
    }
    return n;
  };

  switch (period) {
    case "day":
      return [{ d: "Hoy", sub: hoy.format("D MMM"), n: sumar(hoy, hoy) }];

    case "week": {
      const lunes = hoy.subtract((hoy.day() + 6) % 7, "day");
      return Array.from({ length: 7 }, (_, i) => {
        const d = lunes.add(i, "day");
        return { d: d.format("ddd"), sub: d.format("D"), n: sumar(d, d) };
      });
    }

    case "prev_week": {
      const lunes = hoy.subtract((hoy.day() + 6) % 7, "day").subtract(1, "week");
      return Array.from({ length: 7 }, (_, i) => {
        const d = lunes.add(i, "day");
        return { d: d.format("ddd"), sub: d.format("D"), n: sumar(d, d) };
      });
    }

    case "month":
    case "prev_month": {
      const base = period === "month" ? hoy : hoy.subtract(1, "month");
      const inicio = base.startOf("month");
      const fin = base.endOf("month");
      // Cuatro tramos fijos, el último se estira hasta fin de mes. Da siempre 4 barras, que es lo
      // que pide el diseño, en vez de 4 o 5 según cómo caiga el calendario.
      return [0, 1, 2, 3].map((i) => {
        const desde = inicio.add(i * 7, "day");
        const hasta = i === 3 ? fin : inicio.add(i * 7 + 6, "day");
        return { d: `Sem ${i + 1}`, sub: `${desde.date()}-${hasta.date()}`, n: sumar(desde, hasta) };
      });
    }

    case "year": {
      const inicio = hoy.startOf("year");
      return Array.from({ length: 12 }, (_, i) => {
        const mes = inicio.add(i, "month");
        return { d: mes.format("MMM"), sub: mes.format("YYYY"), n: sumar(mes.startOf("month"), mes.endOf("month")) };
      });
    }

    case "historic":
    default:
      return serie.map((p) => ({ d: dayjs(p.date).format("MMM YY"), n: p.count }));
  }
}

// ── Selección de operadores del Panel de control (xcons-docs#318) ─────────────
//
// Sin toggle "Mi panel": la propia tarjeta es un operador más en `filters.operators`, pre-tildada
// por default. `PUT /dashboard/config` rellena `operators: []` con cualquier guardado del
// dashboard (no sólo al tocar este filtro), así que el server no puede distinguir "nunca elegiste
// nada" de "elegiste que sea nadie" — se guarda esa preferencia puntual acá, en el navegador.

const vaciadosKey = (userId: string) => `xcons.dashboard.operadoresVaciados.${userId}`;

export function operadoresVaciadosAPropósito(userId: string): boolean {
  try {
    return localStorage.getItem(vaciadosKey(userId)) === "1";
  } catch {
    return false;
  }
}

export function marcarOperadoresVaciados(userId: string, vaciado: boolean): void {
  try {
    if (vaciado) localStorage.setItem(vaciadosKey(userId), "1");
    else localStorage.removeItem(vaciadosKey(userId));
  } catch {
    // localStorage puede no estar disponible (ventana privada, etc.) — no hay nada que hacer.
  }
}

/**
 * `operators` guardados, más la propia tarjeta pre-tildada cuando no hay nada guardado todavía y
 * no se vació a propósito. Comparten esto `MetricsPanel` y `MisClientes` (hermanos, misma config).
 */
export function operadoresSeleccionados(
  operatorsGuardados: number[],
  myId: string | null,
): string[] {
  if (operatorsGuardados.length > 0) return operatorsGuardados.map(String);
  if (!myId || operadoresVaciadosAPropósito(myId)) return [];
  return [myId];
}
